import classNames from 'classnames';
import { useEffect, useMemo } from 'react';

import type {
  CompositionV2DataQuery,
  GetSentimentDataQuery,
  PostingsDataQuery,
} from '@revelio/data-access';
import {
  type AreaData,
  FormatType,
  PlotLoadWrapper,
  ReplotChartNames,
  StatTrendChart,
} from '@revelio/replots';

import type { PrimaryFilterId } from '../../../filters/types/filters';
import { CompositionRawDataProvider } from '../../DataProvider/composition/CompositionRawDataProvider';
import { PostingDataProviderRaw } from '../../DataProvider/PostingDataProvider';
import { SentimentDataProviderRaw } from '../../DataProvider/SentimentDataProvider';
import { DataProviderQueryStatus, DataType } from '../../DataProvider/types';
import { convertCompositionData } from '../../DataProvider/utils/convert-composition-data';
import { convertPostingData } from '../../DataProvider/utils/convert-posting-data';
import { convertSentimentData } from '../../DataProvider/utils/convert-sentiment-data';
import { combineQueryStatus } from '../../DataProvider/utils/data-provider-results-utils';
import { ChartFallback } from '../ChartFallback';
import type { MixedStatsData } from '../chart-menu/data-download/converters/mixed-stats-data-converter';
import { usePageExport } from '../chart-menu/PageExportProvider';
import styles from './MixedStats.module.css';

interface MixedStatsProps {
  compositionData: CompositionV2DataQuery['composition_v2'];
  postingsActiveData: PostingsDataQuery['posting'];
  sentimentData: GetSentimentDataQuery['sentiment'];
  loading?: boolean;
}

const MixedStats = ({
  compositionData,
  postingsActiveData,
  sentimentData,
  loading = false,
}: MixedStatsProps) => {
  const { registerChart, unregisterChart } = usePageExport();
  const headcountData = compositionData
    ? getCompositionTimeseries({
        compositionData,
        metric: 'headcount',
      })
    : null;
  const growthRateData = compositionData
    ? getCompositionTimeseries({
        compositionData,
        metric: 'growth_rate',
      })
    : null;

  const postingDataSeries = postingsActiveData
    ? convertPostingData({
        metric: 'postings_active',
        data: postingsActiveData,
        chartType: 'line',
      })
    : null;
  const postingData: AreaData | null = (() => {
    if (postingDataSeries?.type !== DataType.TIMESERIES) return null;
    const firstEntity = postingDataSeries?.data[0];
    if (!firstEntity) return null;

    return {
      label: firstEntity?.dim,
      values: firstEntity?.timeseries.map((t) => ({
        x: t.date,
        value: t.value,
      })),
    };
  })();

  const sentimentOverall = sentimentData
    ? getSentimentTimeseries({
        sentimentData,
        metric: 'overall',
      })
    : null;

  const sentimentBusinessOutlook = sentimentData
    ? getSentimentTimeseries({
        sentimentData,
        metric: 'business_outlook',
      })
    : null;

  const sentimentCompensationsBenefits = sentimentData
    ? getSentimentTimeseries({
        sentimentData,
        metric: 'compensation',
      })
    : null;

  const noData = [
    headcountData,
    growthRateData,
    postingData,
    sentimentOverall,
    sentimentBusinessOutlook,
    sentimentCompensationsBenefits,
  ].every((metric) => metric?.values?.length === 0);

  // Prepare mixed-stats data for export
  const mixedStatsData: MixedStatsData = useMemo(
    () => ({
      headcount: headcountData,
      growthRate: growthRateData,
      postings: postingData,
      sentimentOverall: sentimentOverall,
      sentimentBusinessOutlook: sentimentBusinessOutlook,
      sentimentCompensation: sentimentCompensationsBenefits,
    }),
    [
      headcountData,
      growthRateData,
      postingData,
      sentimentOverall,
      sentimentBusinessOutlook,
      sentimentCompensationsBenefits,
    ]
  );

  // Register chart for export
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (!noData) {
      registerChart('mixed-stats', {
        title: 'Company Statistics',
        chartType: ReplotChartNames.LineChart,
        selectedSegments: [],
        data: mixedStatsData,
        dataType: 'mixedStatsData',
      });
      return () => unregisterChart('mixed-stats');
    }
    return undefined;
  }, [noData, registerChart, unregisterChart, mixedStatsData, loading]);

  return (
    <PlotLoadWrapper loading={loading} noData={noData}>
      <div
        className={classNames(styles.container, styles.fourCharts)}
        data-testid="company-stats-plot-grid"
      >
        <div className={styles.row}>
          <div className={styles.rowItem}>
            <StatTrendChart
              title="Headcount"
              info={
                <p>
                  Our estimate of the total workforce. These counts include
                  subsidiaries and may include both formal employees and
                  contingent workers, depending on your preference settings.
                  More information on the methodologies used for this metric can
                  be found{' '}
                  <a
                    className={styles.infoLink}
                    href="https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights"
                  >
                    here
                  </a>
                  .
                </p>
              }
              data={headcountData}
            />
          </div>
          <div className={styles.rowItem}>
            <StatTrendChart
              title="Growth"
              info="The growth rate is the percent change in the total workforce. It is
          the difference between the hiring rate and attrition rate."
              data={growthRateData}
              format={FormatType.PERCENTAGE}
            />
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.rowItem}>
            <StatTrendChart
              title="Active Postings"
              info="The number of active job postings."
              data={postingData}
            />
          </div>
          <div className={styles.rowItem}>
            <StatTrendChart
              format={FormatType.PERCENTAGE}
              title="Sentiment"
              info="The average score given by workers on the topic of Overall."
              data={sentimentOverall}
            />
          </div>
        </div>
      </div>
    </PlotLoadWrapper>
  );
};

const getCompositionTimeseries = ({
  compositionData,
  metric,
}: {
  compositionData: NonNullable<CompositionV2DataQuery['composition_v2']>;
  metric: 'headcount' | 'growth_rate';
}): AreaData | null => {
  const data = convertCompositionData({
    metric,
    segment: 'timeseries',
    data: compositionData,
  });

  if (!data || data.type !== DataType.TIMESERIES) return null;

  const firstEntity = data.data[0];
  return {
    label: firstEntity?.dim ?? '',
    values:
      firstEntity?.timeseries?.map(
        (t: { date: string; value: number | null }) => ({
          x: t.date,
          value: t.value,
        })
      ) ?? [],
  };
};

const getSentimentTimeseries = ({
  sentimentData,
  metric,
}: {
  sentimentData: NonNullable<GetSentimentDataQuery['sentiment']>;
  metric: 'overall' | 'business_outlook' | 'compensation';
}): AreaData | null => {
  const data = convertSentimentData({
    metric,
    chartType: 'line',
    data: sentimentData,
  });

  if (data.type !== DataType.TIMESERIES) return null;

  const firstEntity = data.data?.[0];

  if (!firstEntity) return null;

  return {
    label: firstEntity.dim,
    values: firstEntity.timeseries.map((t) => ({
      x: t.date,
      value: t.value,
    })),
  };
};

export const MixedStatsChart = ({
  primaryDimension,
}: {
  primaryDimension: PrimaryFilterId;
}) => {
  return (
    <CompositionRawDataProvider
      primaryDimension={primaryDimension}
      isTimeseries
    >
      {({ data: compositionData, status: compositionStatus }) => (
        <PostingDataProviderRaw
          primaryDimension={primaryDimension}
          defaultMetricMode="expected_hires"
        >
          {({ data: postingsData, status: postingsStatus }) => (
            <SentimentDataProviderRaw primaryDimension={primaryDimension}>
              {({ data: sentimentData, status: sentimentStatus }) => {
                const status = combineQueryStatus([
                  compositionStatus,
                  postingsStatus,
                  sentimentStatus,
                ]);

                return (
                  <ChartFallback status={status}>
                    <MixedStats
                      compositionData={compositionData?.composition_v2}
                      postingsActiveData={postingsData?.posting}
                      sentimentData={sentimentData?.sentiment}
                      loading={status === DataProviderQueryStatus.Fetching}
                    />
                  </ChartFallback>
                );
              }}
            </SentimentDataProviderRaw>
          )}
        </PostingDataProviderRaw>
      )}
    </CompositionRawDataProvider>
  );
};
